import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactTypingEffect from 'react-typing-effect';
import './WorkPreviews.css';

const WorkDetail = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const { slug } = useParams();
  const navigate = useNavigate();
  const contentRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch('https://api.jsonbin.io/v3/b/65c7cefddc74654018a30540');
        if (!response.ok) {
          throw new Error('Failed to fetch data');
        }
        const jsonData = await response.json();
        setData(jsonData.record); // Assuming the JSON data is stored in a "record" field
        setCurrentIndex(jsonData.record.findIndex(work => work.slug === slug));
        // Simulating a delay for demonstration (remove in production)
        setTimeout(() => {
          setIsLoading(false); // Hide loading screen after data is fetched
          window.scrollTo(0, 0); // Scroll to top after data is fetched
        }, 500); // Simulating loading delay with setTimeout
      } catch (error) {
        console.error('Error fetching data: ', error);
      }
    };

    fetchData();
  }, [slug]);

  const loadWorkDetail = async (index) => {
    setIsLoading(true); // Show loading screen before fetching new data
    try {
      const response = await fetch('https://api.jsonbin.io/v3/b/65c7cefddc74654018a30540');
      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }
      const jsonData = await response.json();
      setData(jsonData.record); // Assuming the JSON data is stored in a "record" field
      setCurrentIndex(index);
      // Simulating a delay for demonstration (remove in production)
      setTimeout(() => {
        setIsLoading(false); // Hide loading screen after data is fetched
        window.scrollTo(0, 0); // Scroll to top after data is fetched
      }, 500); // Simulating loading delay with setTimeout
    } catch (error) {
      console.error('Error fetching data: ', error);
    }
  };

  const handlePrev = () => {
    const prevIndex = currentIndex === 0 ? data.length - 1 : currentIndex - 1;
    navigate(`/work/${data[prevIndex].slug}`);
    loadWorkDetail(prevIndex);
  };

  const handleNext = () => {
    const nextIndex = currentIndex === data.length - 1 ? 0 : currentIndex + 1;
    navigate(`/work/${data[nextIndex].slug}`);
    loadWorkDetail(nextIndex);
  };

  // Optimize images in HTML content
  useEffect(() => {
    if (!isLoading && data && contentRef.current && data[currentIndex]?.description) {
      const work = data[currentIndex];
      const images = contentRef.current.querySelectorAll('img');
      images.forEach((img) => {
        // Add lazy loading to all images in content (they're below the fold)
        if (!img.hasAttribute('loading')) {
          img.loading = 'lazy';
        }
        // Add decoding for better performance
        if (!img.hasAttribute('decoding')) {
          img.decoding = 'async';
        }
        // Ensure images are responsive
        if (!img.hasAttribute('style') || !img.style.maxWidth) {
          img.style.maxWidth = '100%';
          img.style.height = 'auto';
        }
        // Add load event to mark image as loaded for CSS transition
        if (img.complete) {
          img.classList.add('loaded');
        } else {
          img.addEventListener('load', () => {
            img.classList.add('loaded');
          }, { once: true });
        }
      });
    }
  }, [data, currentIndex, isLoading]);

  // Preload hero image
  useEffect(() => {
    if (!isLoading && data && data[currentIndex]?.heroImage) {
      const heroImage = data[currentIndex].heroImage;
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = heroImage;
      document.head.appendChild(link);
      return () => {
        if (document.head.contains(link)) {
          document.head.removeChild(link);
        }
      };
    }
  }, [data, currentIndex, isLoading]);

  if (isLoading || !data) {
    return (
      <div
        className="loading-screen"
        style={{
          backgroundColor: '#fffdf6',
          height: '100vh',
          width: '100vw',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: 9999,
        }}
      >
          <h2 style={{ color: 'black', fontSize: '50px'}}>
            Loading{' '}
            <ReactTypingEffect
              text={["..."]}
              speed={110}
              eraseDelay={10}
              typingDelay={50}
              cursor=" "
            />
          </h2>
      </div>
    );
  }

  const work = { ...data[currentIndex], html: "<h1>404, you should not be here.</h1>" };

  // Parse description to extract links and images
  const parseDescription = (html) => {
    if (!html) return { githubLinks: [], projectLinks: [], images: [], content: html };
    
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const githubLinks = [];
    const projectLinks = [];
    const images = [];
    const seenUrls = new Set(); // To avoid duplicates
    
    // Extract GitHub links - try multiple selectors to catch all variations
    doc.querySelectorAll('a').forEach(link => {
      const href = link.getAttribute('href');
      if (href && href.toLowerCase().includes('github.com') && !seenUrls.has(href)) {
        seenUrls.add(href);
        githubLinks.push({
          url: href,
          text: link.textContent.trim() || 'View on GitHub'
        });
      }
    });
    
    // Extract project/live links (not GitHub - include Google Drive links)
    doc.querySelectorAll('a').forEach(link => {
      const href = link.getAttribute('href');
      if (href && !href.toLowerCase().includes('github.com') && !seenUrls.has(href)) {
        seenUrls.add(href);
        // Determine the label based on link type
        let linkText = 'View Live Project';
        if (href.toLowerCase().includes('drive.google.com')) {
          linkText = 'View Demo/Documentation';
        }
        projectLinks.push({
          url: href,
          text: link.textContent.trim() || linkText
        });
      }
    });
    
    // Extract images with their context
    doc.querySelectorAll('img').forEach(img => {
      images.push({
        src: img.src,
        alt: img.alt || 'Project image',
        className: img.className
      });
    });
    
    // Get text content without links and images
    const tempDiv = doc.cloneNode(true);
    tempDiv.querySelectorAll('a, img').forEach(el => el.remove());
    // REMOVE ALL H1 TAGS - hard-coded removal
    tempDiv.querySelectorAll('h1').forEach(el => el.remove());
    let content = tempDiv.body.innerHTML;
    
    // Make "Technologies:" bold
    content = content.replace(/Technologies:/g, '<strong>Technologies:</strong>');
    
    return { githubLinks, projectLinks, images, content };
  };

  const parsedData = parseDescription(work.description);
  
  // Debug: Log the parsed links
  console.log('GitHub Links:', parsedData.githubLinks);
  console.log('Project Links:', parsedData.projectLinks);
  
  // Filter links based on project
  let githubLinks = parsedData.githubLinks;
  let projectLinks = parsedData.projectLinks;
  
  // Rabat Doc: Show only website link, NO GitHub
  if (work.slug === 'rabat-doc') {
    githubLinks = [];
  }
  
  // Scia-Tech: Hard-code the exact 2 links
  if (work.slug === 'scia-tech') {
    githubLinks = [
      {
        url: 'https://github.com/RabiiAlaouiLamharzi/scia-tech-mobile',
        text: 'View Code on GitHub'
      }
    ];
    projectLinks = [
      {
        url: 'https://drive.google.com/file/d/1TVjc-qn72xgn6nlSPqopvPkXDN8YUF_8/view',
        text: 'View Demo/Documentation'
      }
    ];
  }

  // Get project-specific problem description
  const getProblemDescription = (slug, type, date, client, progress, excerpt) => {
    switch (slug) {
      case 'rabat-doc':
        return `The client ${client} required a comprehensive digital solution for the Rabat Doc Africa International Festival. The festival, held from April 25 to 29, aligns with the theme 'African Documentary Film: A Catalyst for Creativity and Development' and is part of the festivities marking Rabat as the African Capital of Culture for 2022/2023. This ${type} project needed to showcase the festival's award-winning films, manage content through an admin platform, and provide an engaging experience for festival attendees.`;
      
      case 'smha':
        return `Al Akhawayn University’s campus, though rich in resources and facilities, operates like a self-contained city far from most students’ hometowns. This unique environment can make students feel isolated and disconnected, affecting both their social experience and overall mental well-being. As part of my capstone project, and under the guidance of Dr. Houda Chakiri, I proposed the Student Mental Health App as a transformative solution to address the nuanced mental health needs of AUI students, aiming to provide support and connection within the university community.`;
      
      case 'scia-tech':
        return `I was honored to be selected as one of 24 students out of more than 800 applicants for a three-month internship at Orange Maroc. The internship took the form of a competition, where four teams of six members each were challenged to design and develop a project from start to finish. My team’s project, Scia-Tech, focused on creating smart insoles to assist individuals suffering from sciatica.`;
      
      case 'autoone':
        return `As part of a freelance project, a used car dealer in Grez-Doiceau, Belgium, needed a modern, professional website to strengthen their online presence and attract customers. The site had to clearly communicate the garage’s expertise and reliability, reflect their passion for cars, and provide a smooth, responsive experience across devices.`;
      
      case 'saga':
        return `This project addresses a specific challenge I identified at Al Akhawayn University. This ${type} project creates a centralized search engine to help students, faculty, and staff quickly find information about academic resources, administrative services, and campus events. ${excerpt}`;
      
      default:
        return excerpt || 'Project description coming soon.';
    }
  };

  return (
    <div className="flex flex-col justify-center items-center">
      <div className="items-start">
        <img
          src={work.heroImage}
          alt={work.title}
          className="mx-auto"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          style={{
            width: "70vw",
            height: "70vh",
            objectFit: "cover",
            borderRadius: "0 0 42px 42px",
            marginBottom: "50px",
            alignSelf: "flex-start"
          }}
        />
      </div>
      <div className="flex flex-col items-start" style={{width:'70vw'}}>
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
          {work.title}
        </h1>
        <hr className="w-full border-green-100 mb-9 opacity-20" />
      </div>

      {/* Case Study Sections */}
      <section className="my-12" style={{width:'70vw'}}>
        
        {/* Problem Section */}
        <div className="mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-black">Understanding the Challenge</h2>
          <div className="text-black text-justify leading-relaxed">
            <p className="text-lg mb-4">
              {getProblemDescription(work.slug, work.type, work.date, work.client, work.progress, work.excerpt)}
            </p>
          </div>
        </div>

        {/* Process Section */}
        <div className="mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-black">Design & Development</h2>
          <div className="text-black text-justify leading-relaxed mb-8">
            {work.slug === 'autoone' ? (
              <div ref={contentRef} className="max-full mx-auto json-html text-black text-justify mb-8">
                <p className="text-lg mb-4">
                  The landing page, custom-designed for Garage Auto One, is organized into several clear sections to present the business effectively. It includes a testimonial section to highlight customer satisfaction, a pop-up area showcasing the variety of services offered, and an "About Us" section to introduce the garage and its team of skilled mechanics.
                </p>
              </div>
            ) : (
              <div
                ref={contentRef}
                dangerouslySetInnerHTML={{ __html: parsedData.content }}
                className="max-full mx-auto json-html text-black text-justify mb-8"
              />
            )}
          </div>
          
          {/* Progress Images */}
          {parsedData.images.length > 0 && (
            <div className="space-y-12">
             {parsedData.images.map((img, index) => {
               const imgSrc = img.src.toLowerCase();
               
               // Skip icon images
               if (img.className?.includes('small-image1') || imgSrc.includes('github')) {
                 return null;
               } else if (img.className?.includes('small-image2') || imgSrc.includes('resize')) {
                 return null;
               }
               
               return (
                 <div key={index} className="mb-8">
                   <img
                     src={img.src}
                     alt={img.alt}
                     loading="lazy"
                     decoding="async"
                     className="w-full h-auto rounded-lg shadow-lg"
                   />
                 </div>
               );
             }).filter(Boolean)}
            </div>
          )}
        </div>

        {/* Solution Section */}
        <div className="mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-black">The Result</h2>
          <div className="text-black text-justify leading-relaxed mb-8">
            {(githubLinks.length > 0 || projectLinks.length > 0) && (
              <p className="text-lg mb-6">
                For more information about the project, you can check out the following links:
              </p>
            )}
          </div>

          {/* GitHub and Project Links */}
          <div className="flex flex-wrap gap-4 mb-8">
            {githubLinks.map((link, index) => (
              <a
                key={index}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-black text-white rounded-full hover:bg-gray-800 transition duration-300 font-medium"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.17 6.839 9.49.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.463-1.11-1.463-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.336 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.167 22 16.418 22 12c0-5.523-4.477-10-10-10z" clipRule="evenodd" />
                </svg>
                <span>View Code on GitHub</span>
              </a>
            ))}
            {projectLinks.map((link, index) => (
              <a
                key={index}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 border-2 border-black text-black rounded-full hover:bg-black hover:text-white transition duration-300 font-medium"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                <span>{link.text}</span>
              </a>
            ))}
          </div>
        </div>

        <hr className="w-full border-green-100 mt-9 opacity-20" />
        <div className="w-full mt-6 flex justify-between">
          <button
            onClick={handlePrev}
            className="bg-transparent text-black font-medium py-2 px-4 mr-4"
          >
            <h2 className='nextandprev'>Prev</h2>
          </button>
          <button
            onClick={handleNext}
            className="bg-transparent text-black font-medium py-2 px-4 "
          >
            <h2 className='nextandprev'>Next</h2>
          </button>
        </div>
      </section>
    </div>
  );
};

export default WorkDetail;
